'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type {
  CrawlerSessionResponseSchema,
  CrawlerSessionStatsSchema,
} from '@terra/shared-contracts-applications';
import {
  CRAWLER_SESSION_STARTED_SSE_EVENT,
  CRAWLER_SESSION_STATS_UPDATED_SSE_EVENT,
  CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT,
} from '@terra/shared-contracts-applications';
import type { z } from 'zod';
import { getAuthHeaders } from '../utils/authHeaders';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { useProjectStore } from '../store/projectStore';
import { useSSE } from '../app/providers/SseProvider';
import {
  clearAbortUiEscapeTimeout,
  syncAbortUiEscapeTimeout,
} from './abort-ui-escape';

export type CrawlerSessionType =
  | 'SHALLOW'
  | 'AI'
  | 'MANUAL'
  | 'AUTHENTICATION'
  | 'FEATURE_ANALYSIS'
  | 'NAVIGATION_PROFILE';
export type CrawlerSessionStatusType =
  | 'RUNNING'
  | 'ABORTING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABORTED';

export const ACTIVE_STATUSES: CrawlerSessionStatusType[] = [
  'RUNNING',
  'ABORTING',
];

export const ABORTING_UI_ESCAPE_MS = 45_000;

const TERMINAL_SESSION_LINGER_MS = 4000;

export function isActiveSession(session: CrawlerSession): boolean {
  return ACTIVE_STATUSES.includes(session.status);
}

export function isAbortUiLocked({
  isLocallyAborting,
  allActiveAborting,
  abortingSinceMs,
  nowMs,
}: {
  isLocallyAborting: boolean;
  allActiveAborting: boolean;
  abortingSinceMs: number | null;
  nowMs: number;
}): boolean {
  if (!isLocallyAborting && !allActiveAborting) {
    return false;
  }

  if (abortingSinceMs == null) {
    return true;
  }

  return nowMs - abortingSinceMs < ABORTING_UI_ESCAPE_MS;
}

export type CrawlerSession = z.infer<typeof CrawlerSessionResponseSchema> & {
  tenantId?: string;
};

export type CrawlerSessionStats = z.infer<typeof CrawlerSessionStatsSchema>;

export interface GetSessionsResponse {
  sessions: CrawlerSession[];
  stats: Record<string, CrawlerSessionStats>;
}

export interface HistoryFilters {
  crawlerType?: CrawlerSessionType;
  crawlerTypes?: CrawlerSessionType[];
  trigger?: 'MANUAL' | 'CRAWLER';
  credentialId?: string | null;
  credentialIds?: Array<string | null>;
  fromDate?: string;
  toDate?: string;
}

export interface GetHistoryResponse {
  sessions: Array<CrawlerSession & { stats: CrawlerSessionStats }>;
  nextCursor?: string;
  hasMore: boolean;
}

interface UseCrawlerSessionsOptions {
  applicationId: string | undefined;
  enabled?: boolean;
}

export type CrawlerCredentialLastMeta = {
  finishedAt: string;
  crawlerType: CrawlerSessionType;
};

interface UseCrawlerSessionsReturn {
  sessions: CrawlerSession[];
  statsBySession: Record<string, CrawlerSessionStats>;
  sessionsByCredential: Record<string, CrawlerSession[]>;
  lastFinishedMetaByCredential: Record<string, CrawlerCredentialLastMeta>;
  isLoading: boolean;
  hasActiveSessions: boolean;
  activeSessionCount: number;
  abortSession: (sessionId: string) => Promise<void>;
  abortByCredential: (credentialId: string | null) => Promise<void>;
  abortAll: () => Promise<void>;
  refetch: () => Promise<{ data: GetSessionsResponse | undefined }>;
  abortingSessionIds: Set<string>;
  abortingCredentialIds: Set<string>;
  isAbortingAll: boolean;
}

export function useCrawlerSessions({
  applicationId,
  enabled = true,
}: UseCrawlerSessionsOptions): UseCrawlerSessionsReturn {
  const queryClient = useQueryClient();
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const { subscribe, tenantId: sseTenantId } = useSSE();
  const tenantId = sseTenantId ?? selectedCompany?.tenantId;
  const [abortingSessionIds, setAbortingSessionIds] = useState<Set<string>>(
    new Set(),
  );
  const [abortingCredentialIds, setAbortingCredentialIds] = useState<
    Set<string>
  >(new Set());
  const [isAbortingAll, setIsAbortingAll] = useState(false);
  const [liveStatsBySession, setLiveStatsBySession] = useState<
    Record<string, CrawlerSessionStats>
  >({});
  const [lastFinishedMetaByCredential, setLastFinishedMetaByCredential] =
    useState<Record<string, CrawlerCredentialLastMeta>>({});
  const abortUiEscapeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const sessionsQuery = useQuery({
    queryKey: ['crawler-sessions', applicationId, tenantId],
    queryFn: async (): Promise<GetSessionsResponse> => {
      if (!applicationId || !tenantId) {
        return { sessions: [], stats: {} };
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.getCrawlerSessions({
        params: { id: applicationId },
        body: {},
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status === 200) {
        const resolvedTenantId = tenantId;
        const result = {
          sessions: response.body.sessions.map((s) => ({
            ...s,
            tenantId: resolvedTenantId,
          })),
          stats: response.body.stats,
        };

        return result;
      }

      throw new Error('Failed to fetch crawler sessions');
    },
    enabled: !!applicationId && !!tenantId && enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      if (!applicationId || !tenantId || !enabled) {
        return false;
      }

      const hasActive = (query.state.data?.sessions ?? []).some(
        isActiveSession,
      );

      return hasActive ? 30_000 : false;
    },
  });

  const terminalEvictionTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());

  useEffect(() => {
    if (!applicationId || !tenantId || !enabled) {
      return;
    }

    setLiveStatsBySession({});
    setLastFinishedMetaByCredential({});

    const queryKey = ['crawler-sessions', applicationId, tenantId] as const;
    const evictionTimers = terminalEvictionTimersRef.current;

    const clearEvictionTimer = (sessionId: string) => {
      const timer = evictionTimers.get(sessionId);

      if (timer) {
        clearTimeout(timer);
        evictionTimers.delete(sessionId);
      }
    };

    const scheduleEviction = (sessionId: string) => {
      clearEvictionTimer(sessionId);
      const timer = setTimeout(() => {
        evictionTimers.delete(sessionId);
        queryClient.setQueryData<GetSessionsResponse | undefined>(
          queryKey,
          (previous) => {
            if (!previous) {
              return previous;
            }

            const hasSession = previous.sessions.some(
              (session) => session.id === sessionId,
            );
            const hasStats = previous.stats[sessionId] !== undefined;

            if (!hasSession && !hasStats) {
              return previous;
            }

            const nextStats = { ...previous.stats };

            delete nextStats[sessionId];
            setLiveStatsBySession((currentLiveStats) => {
              if (currentLiveStats[sessionId] === undefined) {
                return currentLiveStats;
              }

              const nextLiveStats = { ...currentLiveStats };

              delete nextLiveStats[sessionId];

              return nextLiveStats;
            });

            return {
              ...previous,
              sessions: previous.sessions.filter(
                (session) => session.id !== sessionId,
              ),
              stats: nextStats,
            };
          },
        );
      }, TERMINAL_SESSION_LINGER_MS);

      evictionTimers.set(sessionId, timer);
    };

    const refetchSessions = () => {
      void queryClient.refetchQueries({ queryKey });
    };

    const unsubscribeStarted = subscribe(
      CRAWLER_SESSION_STARTED_SSE_EVENT,
      (event) => {
        let shouldRefetch = false;

        queryClient.setQueryData<GetSessionsResponse | undefined>(
          queryKey,
          (previous) => {
            if (!previous) {
              shouldRefetch = true;

              return previous;
            }

            if (previous.sessions.some((session) => session.id === event.id)) {
              return previous;
            }

            return {
              ...previous,
              sessions: [
                ...previous.sessions,
                {
                  id: event.id,
                  applicationId: event.targetId,
                  credentialId: event.credentialId,
                  userId: event.userId,
                  parentId: event.parentId,
                  crawlerType: event.crawlerType,
                  status: event.status,
                  createdAt: event.createdAt,
                  lastHeartbeatAt: event.lastHeartbeatAt,
                  finishedAt: null,
                  error: null,
                  tenantId,
                },
              ],
            };
          },
        );

        if (shouldRefetch) {
          refetchSessions();
        }
      },
      { filter: (event) => event.targetId === applicationId },
    );

    const unsubscribeStatusChanged = subscribe(
      CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT,
      (event) => {
        const isTerminal = !ACTIVE_STATUSES.includes(event.status);
        let shouldRefetch = false;
        const cachedBeforeUpdate = queryClient.getQueryData<
          GetSessionsResponse | undefined
        >(queryKey);
        const existingSession = cachedBeforeUpdate?.sessions.find(
          (session) => session.id === event.sessionId,
        );

        queryClient.setQueryData<GetSessionsResponse | undefined>(
          queryKey,
          (previous) => {
            if (!previous) {
              shouldRefetch = true;

              return previous;
            }

            const sessionExists = previous.sessions.some(
              (session) => session.id === event.sessionId,
            );

            if (!sessionExists) {
              shouldRefetch = true;

              return previous;
            }

            const nextSessions = previous.sessions.map((session) =>
              session.id === event.sessionId
                ? {
                    ...session,
                    status: event.status,
                    finishedAt: event.finishedAt ?? null,
                    error: event.error ?? null,
                  }
                : session,
            );

            return {
              ...previous,
              sessions: nextSessions,
            };
          },
        );

        if (isTerminal) {
          const finishedAt =
            event.finishedAt ??
            existingSession?.finishedAt ??
            new Date().toISOString();
          const credentialKey = event.credentialId ?? 'anonymous';
          const crawlerType = existingSession?.crawlerType ?? 'SHALLOW';

          setLastFinishedMetaByCredential((previous) => {
            const current = previous[credentialKey];

            if (
              current &&
              new Date(current.finishedAt) >= new Date(finishedAt)
            ) {
              return previous;
            }

            return {
              ...previous,
              [credentialKey]: { finishedAt, crawlerType },
            };
          });
          void queryClient.invalidateQueries({
            queryKey: ['crawler-history', applicationId],
          });
          void queryClient.invalidateQueries({
            queryKey: ['crawler-last-finished-by-credential', applicationId],
          });
          scheduleEviction(event.sessionId);
        } else {
          clearEvictionTimer(event.sessionId);
        }

        if (shouldRefetch) {
          refetchSessions();
        }
      },
      { filter: (event) => event.targetId === applicationId },
    );

    const unsubscribeStatsUpdated = subscribe(
      CRAWLER_SESSION_STATS_UPDATED_SSE_EVENT,
      (event) => {
        setLiveStatsBySession((previous) => ({
          ...previous,
          [event.sessionId]: event.stats,
        }));

        const hadCache =
          queryClient.getQueryData<GetSessionsResponse | undefined>(
            queryKey,
          ) !== undefined;

        if (!hadCache) {
          refetchSessions();
        }
      },
      { filter: (event) => event.targetId === applicationId },
    );

    return () => {
      unsubscribeStarted();
      unsubscribeStatusChanged();
      unsubscribeStatsUpdated();
      evictionTimers.forEach((timer) => clearTimeout(timer));
      evictionTimers.clear();
    };
  }, [applicationId, enabled, queryClient, subscribe, tenantId]);

  const stopMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      if (!applicationId) throw new Error('Application ID required');

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.abortCrawlerSession({
        params: { id: applicationId },
        body: { crawlerSessionId: sessionId },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status === 200) {
        if (!response.body.success) {
          throw new Error(
            response.body.message || 'Failed to stop crawler session',
          );
        }

        return response.body;
      }

      throw new Error('Failed to stop crawler session');
    },
    onMutate: async (sessionId) => {
      setAbortingSessionIds((prev) => new Set(prev).add(sessionId));
    },
    onError: (_, sessionId) => {
      setAbortingSessionIds((prev) => {
        const next = new Set(prev);

        next.delete(sessionId);

        return next;
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['crawler-sessions', applicationId],
      });
    },
  });

  const abortByCredentialMutation = useMutation({
    mutationFn: async (credentialId: string | null) => {
      if (!applicationId) throw new Error('Application ID required');

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.abortCrawlerSession({
        params: { id: applicationId },
        body: { credentialId },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status === 200) {
        if (!response.body.success) {
          throw new Error(
            response.body.message || 'Failed to abort crawler sessions',
          );
        }

        return response.body;
      }

      throw new Error('Failed to abort crawler sessions');
    },
    onMutate: async (credentialId) => {
      const trackingId = credentialId || 'anonymous';

      setAbortingCredentialIds((prev) => new Set(prev).add(trackingId));
    },
    onError: (_, credentialId) => {
      const trackingId = credentialId || 'anonymous';

      setAbortingCredentialIds((prev) => {
        const next = new Set(prev);

        next.delete(trackingId);

        return next;
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['crawler-sessions', applicationId],
      });
    },
  });

  const abortAllMutation = useMutation({
    mutationFn: async () => {
      if (!applicationId) throw new Error('Application ID required');

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.abortCrawlerSession({
        params: { id: applicationId },
        body: { abortAll: true },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status === 200) {
        if (!response.body.success) {
          throw new Error(
            response.body.message || 'Failed to abort crawler sessions',
          );
        }

        return response.body;
      }

      throw new Error('Failed to abort crawler sessions');
    },
    onMutate: async () => {
      const activeCredentialIds = new Set<string>();
      let previousAbortingCredentialIds = new Set<string>();

      for (const session of sessionsQuery.data?.sessions ?? []) {
        if (isActiveSession(session) && session.crawlerType !== 'MANUAL') {
          activeCredentialIds.add(session.credentialId || 'anonymous');
        }
      }

      setIsAbortingAll(true);
      setAbortingCredentialIds((prev) => {
        previousAbortingCredentialIds = prev;
        const next = new Set(prev);

        for (const trackingId of activeCredentialIds) {
          next.add(trackingId);
        }

        return next;
      });

      return { previousAbortingCredentialIds };
    },
    onError: (_error, _variables, context) => {
      setIsAbortingAll(false);
      setAbortingCredentialIds(
        context?.previousAbortingCredentialIds ?? new Set(),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['crawler-sessions', applicationId],
      });
    },
  });

  const sessions = useMemo(
    () => sessionsQuery.data?.sessions ?? [],
    [sessionsQuery.data?.sessions],
  );

  useEffect(() => {
    setAbortingSessionIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }

      const next = new Set(prev);
      let changed = false;

      for (const sessionId of prev) {
        const session = sessions.find((item) => item.id === sessionId);

        if (!session || !isActiveSession(session)) {
          next.delete(sessionId);
          changed = true;
        }
      }

      return changed ? next : prev;
    });

    setAbortingCredentialIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }

      const next = new Set(prev);
      let changed = false;

      for (const credentialTrackingId of prev) {
        const stillActive = sessions.some(
          (session) =>
            (session.credentialId || 'anonymous') === credentialTrackingId &&
            isActiveSession(session) &&
            session.crawlerType !== 'MANUAL',
        );

        if (!stillActive) {
          next.delete(credentialTrackingId);
          changed = true;
        }
      }

      return changed ? next : prev;
    });

    const hasActiveAutomated = sessions.some(
      (session) => isActiveSession(session) && session.crawlerType !== 'MANUAL',
    );

    if (isAbortingAll && !hasActiveAutomated) {
      setIsAbortingAll(false);
    }
  }, [sessions, isAbortingAll]);

  useEffect(() => {
    syncAbortUiEscapeTimeout({
      isAbortUiActive:
        abortingCredentialIds.size > 0 ||
        abortingSessionIds.size > 0 ||
        isAbortingAll,
      timeoutRef: abortUiEscapeTimeoutRef,
      escapeMs: ABORTING_UI_ESCAPE_MS,
      onEscape: () => {
        setAbortingCredentialIds(new Set());
        setAbortingSessionIds(new Set());
        setIsAbortingAll(false);
      },
    });
  }, [abortingCredentialIds, abortingSessionIds, isAbortingAll]);

  useEffect(() => {
    return () => {
      clearAbortUiEscapeTimeout(abortUiEscapeTimeoutRef);
    };
  }, []);

  const statsBySession = useMemo(
    () => ({
      ...(sessionsQuery.data?.stats ?? {}),
      ...liveStatsBySession,
    }),
    [liveStatsBySession, sessionsQuery.data?.stats],
  );

  const sessionsByCredential = useMemo(() => {
    const grouped: Record<string, CrawlerSession[]> = {};

    sessions.forEach((session) => {
      const key = session.credentialId || 'anonymous';

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(session);
    });

    return grouped;
  }, [sessions]);

  const activeSessionCount = sessions.filter(isActiveSession).length;
  const hasActiveSessions = activeSessionCount > 0;

  const abortSession = useCallback(
    async (sessionId: string) => {
      await stopMutation.mutateAsync(sessionId);
    },
    [stopMutation],
  );

  const abortByCredential = useCallback(
    async (credentialId: string | null) => {
      const trackingId = credentialId || 'anonymous';

      if (isAbortingAll || abortingCredentialIds.has(trackingId)) {
        return;
      }

      await abortByCredentialMutation.mutateAsync(credentialId);
    },
    [abortByCredentialMutation, abortingCredentialIds, isAbortingAll],
  );

  const abortAll = useCallback(async () => {
    if (isAbortingAll) {
      return;
    }

    await abortAllMutation.mutateAsync();
  }, [abortAllMutation, isAbortingAll]);

  const refetch = useCallback(async () => {
    return await sessionsQuery.refetch();
  }, [sessionsQuery]);

  return {
    sessions,
    statsBySession,
    sessionsByCredential,
    lastFinishedMetaByCredential,
    isLoading: sessionsQuery.isLoading,
    hasActiveSessions,
    activeSessionCount,
    abortSession,
    abortByCredential,
    abortAll,
    refetch,
    abortingSessionIds,
    abortingCredentialIds,
    isAbortingAll,
  };
}

interface UseCrawlerHistoryOptions {
  applicationId: string | undefined;
  filters?: HistoryFilters;
  enabled?: boolean;
}

interface UseCrawlerHistoryReturn {
  sessions: Array<CrawlerSession & { stats: CrawlerSessionStats }>;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useCrawlerHistory({
  applicationId,
  filters,
  enabled = true,
}: UseCrawlerHistoryOptions): UseCrawlerHistoryReturn {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;
  const [cursor, setCursor] = useState<string | undefined>();
  const [olderSessions, setOlderSessions] = useState<
    Array<CrawlerSession & { stats: CrawlerSessionStats }>
  >([]);
  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    setCursor(undefined);
    setOlderSessions([]);
  }, [filtersKey, applicationId, tenantId, enabled]);

  const fetchHistoryPage = async (
    pageCursor: string | undefined,
  ): Promise<GetHistoryResponse> => {
    if (!applicationId || !tenantId) {
      return { sessions: [], hasMore: false };
    }

    const authHeaders = await getAuthHeaders();
    const response = await applicationsClient.getCrawlerHistory({
      params: { id: applicationId },
      body: {
        filters,
        cursor: pageCursor,
        limit: 20,
      },
      overrideClientOptions: {
        baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
      },
      extraHeaders: {
        ...authHeaders,
      },
    });

    if (response.status === 200) {
      const resolvedTenantId = tenantId;

      return {
        ...response.body,
        sessions: response.body.sessions.map((session) => ({
          ...session,
          tenantId: resolvedTenantId,
        })),
      };
    }

    throw new Error('Failed to fetch crawler history');
  };

  const firstPageQuery = useQuery({
    queryKey: ['crawler-history', applicationId, tenantId, filters, undefined],
    queryFn: () => fetchHistoryPage(undefined),
    enabled: !!applicationId && !!tenantId && enabled,
  });

  const nextPageQuery = useQuery({
    queryKey: ['crawler-history', applicationId, tenantId, filters, cursor],
    queryFn: () => fetchHistoryPage(cursor),
    enabled: !!applicationId && !!tenantId && enabled && !!cursor,
  });

  useEffect(() => {
    if (!cursor || !nextPageQuery.data?.sessions) {
      return;
    }

    const firstPageIds = new Set(
      (firstPageQuery.data?.sessions ?? []).map((session) => session.id),
    );

    setOlderSessions((prev) => {
      const existingIds = new Set(prev.map((session) => session.id));
      const newSessions = nextPageQuery.data.sessions.filter(
        (session) =>
          !existingIds.has(session.id) && !firstPageIds.has(session.id),
      );

      if (newSessions.length === 0) {
        return prev;
      }

      return [...prev, ...newSessions];
    });
  }, [nextPageQuery.data?.sessions, cursor, firstPageQuery.data?.sessions]);

  const sessions = useMemo(() => {
    const firstPage = firstPageQuery.data?.sessions ?? [];

    if (!cursor) {
      return firstPage;
    }

    const firstPageIds = new Set(firstPage.map((session) => session.id));
    const older = olderSessions.filter(
      (session) => !firstPageIds.has(session.id),
    );

    return [...firstPage, ...older];
  }, [cursor, firstPageQuery.data?.sessions, olderSessions]);

  const loadMore = useCallback(async () => {
    const activePage = cursor ? nextPageQuery.data : firstPageQuery.data;
    const nextCursor = activePage?.nextCursor;

    if (!nextCursor) {
      return;
    }

    setCursor(nextCursor);
  }, [cursor, firstPageQuery.data, nextPageQuery.data]);

  const refetch = useCallback(async () => {
    setCursor(undefined);
    setOlderSessions([]);
    await firstPageQuery.refetch();
  }, [firstPageQuery]);

  return {
    sessions,
    isLoading:
      firstPageQuery.isLoading || (!!cursor && nextPageQuery.isLoading),
    hasMore: cursor
      ? (nextPageQuery.data?.hasMore ?? false)
      : (firstPageQuery.data?.hasMore ?? false),
    loadMore,
    refetch,
  };
}
